import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'react-hot-toast'
import { useAuth } from '@/features/auth/AuthProvider'
import { api } from '@/lib/api'
import type { Profile, Rol } from '@/types/database'
import { CloseIcon, PlusIcon, EditIcon, ChevronDownIcon } from '@/components/shared/Icons'

const ROL_COLORS: Record<string, string> = {
  administrador: 'me-pill-acid',
  cajero:        'me-pill-ok',
  deposito:      'me-pill-neutral',
  readonly:      'me-pill-neutral',
}

// ─── Hooks ────────────────────────────────────────────────────────────────────

type UsuarioFlat = {
  id: string; nombre: string; email: string | null; activo: boolean
  role_id: string; deposito_id: string | null; created_at: string; role_nombre: string; nivel: number
}

function useRoles() {
  return useQuery({
    queryKey: ['roles'],
    queryFn: () => api.get<Rol[]>('/api/auth/roles'),
  })
}

function useUsuarios() {
  return useQuery({
    queryKey: ['usuarios'],
    queryFn: async () => {
      const rows = await api.get<UsuarioFlat[]>('/api/auth/usuarios')
      return rows.map(r => ({
        ...r,
        deposito_id: r.deposito_id ?? null,
        updated_at: r.created_at,
        roles: { id: r.role_id, nombre: r.role_nombre, nivel: r.nivel, created_at: r.created_at },
      })) as Profile[]
    },
  })
}

// ─── Tipos de formularios ─────────────────────────────────────────────────────

interface CreateForm {
  nombre:   string
  email:    string
  password: string
  role_id:  string
}

interface EditForm {
  nombre:  string
  role_id: string
  activo:  boolean
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function UsuariosPage() {
  const qc = useQueryClient()
  const { user } = useAuth()
  const { data: usuarios = [], isLoading } = useUsuarios()
  const { data: roles = [] } = useRoles()

  const [showCreate, setShowCreate]             = useState(false)
  const [editTarget, setEditTarget]             = useState<Profile | null>(null)
  const [lastCreds, setLastCreds]               = useState<{ nombre: string; email: string; password: string } | null>(null)

  const activos   = usuarios.filter(u => u.activo).length
  const inactivos = usuarios.filter(u => !u.activo).length

  // ─── Mutación crear usuario ───────────────────────────────────────────────

  const crearUsuario = useMutation({
    mutationFn: async (form: CreateForm) => {
      await api.post('/api/auth/usuarios', {
        email: form.email,
        password: form.password,
        nombre: form.nombre,
        role_id: form.role_id,
      })
      return form
    },
    onSuccess: (form) => {
      qc.invalidateQueries({ queryKey: ['usuarios'] })
      setShowCreate(false)
      setLastCreds({ nombre: form.nombre, email: form.email, password: form.password })
    },
    onError: (err: unknown) => {
      toast.error(err instanceof Error ? err.message : 'Error al crear usuario')
    },
  })

  // ─── Mutación editar usuario ──────────────────────────────────────────────

  const editarUsuario = useMutation({
    mutationFn: async ({ id, form }: { id: string; form: EditForm }) => {
      if (id === user?.id && !form.activo) {
        throw new Error('No podés desactivar tu propio usuario')
      }
      await api.put(`/api/auth/usuarios/${id}`, { nombre: form.nombre, role_id: form.role_id, activo: form.activo })
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['usuarios'] })
      toast.success('Usuario actualizado')
      setEditTarget(null)
    },
    onError: (err: unknown) => {
      toast.error(err instanceof Error ? err.message : 'Error al actualizar')
    },
  })

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', marginBottom: 24 }}>
        <div>
          <div className="me-eyebrow" style={{ marginBottom: 4 }}>Administración</div>
          <h1 className="me-page-title" style={{ marginBottom: 0 }}>Usuarios</h1>
        </div>
        <button
          className="me-btn me-btn-primary"
          style={{ display: 'flex', alignItems: 'center', gap: 6 }}
          onClick={() => setShowCreate(true)}
        >
          <PlusIcon size={16} />
          Nuevo usuario
        </button>
      </div>

      {/* KPIs */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 24 }}>
        <div className="me-card" style={{ flex: 1, padding: '14px 18px' }}>
          <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.07em', textTransform: 'uppercase', color: 'var(--me-text-3)', marginBottom: 4 }}>
            Total usuarios
          </div>
          <div style={{ fontFamily: 'var(--me-mono)', fontFeatureSettings: '"tnum"', fontSize: 28, fontWeight: 700, letterSpacing: '-0.02em' }}>
            {usuarios.length}
          </div>
        </div>
        <div className="me-card" style={{ flex: 1, padding: '14px 18px' }}>
          <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.07em', textTransform: 'uppercase', color: 'var(--me-text-3)', marginBottom: 4 }}>
            Activos
          </div>
          <div style={{ fontFamily: 'var(--me-mono)', fontFeatureSettings: '"tnum"', fontSize: 28, fontWeight: 700, letterSpacing: '-0.02em', color: 'var(--me-ok)' }}>
            {activos}
          </div>
        </div>
        {inactivos > 0 && (
          <div className="me-card" style={{ flex: 1, padding: '14px 18px' }}>
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.07em', textTransform: 'uppercase', color: 'var(--me-text-3)', marginBottom: 4 }}>
              Inactivos
            </div>
            <div style={{ fontFamily: 'var(--me-mono)', fontFeatureSettings: '"tnum"', fontSize: 28, fontWeight: 700, letterSpacing: '-0.02em', color: 'var(--me-text-3)' }}>
              {inactivos}
            </div>
          </div>
        )}
      </div>

      {/* Tabla */}
      <div className="me-card" style={{ padding: 0, overflow: 'clip' }}>
        {isLoading ? (
          <div style={{ padding: 48, textAlign: 'center', color: 'var(--me-text-3)', fontSize: 13.5 }}>
            Cargando usuarios…
          </div>
        ) : usuarios.length === 0 ? (
          <div style={{ padding: 48, textAlign: 'center', color: 'var(--me-text-3)', fontSize: 13.5 }}>
            Sin usuarios — creá el primero con el botón de arriba
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table className="me-table" style={{ width: '100%' }}>
              <thead>
                <tr>
                  <th>Nombre</th>
                  <th>Email</th>
                  <th>Rol</th>
                  <th>Estado</th>
                  <th>Alta</th>
                  <th style={{ width: 44 }} />
                </tr>
              </thead>
              <tbody>
                {usuarios.map((u, i) => (
                  <tr key={u.id} style={{ background: i % 2 === 0 ? 'transparent' : 'var(--me-surface-2)' }}>
                    <td style={{ fontWeight: 500 }}>
                      {u.nombre}
                      {u.id === user?.id && (
                        <span style={{ marginLeft: 6, fontSize: 10, color: 'var(--me-text-4)', fontWeight: 400 }}>
                          (vos)
                        </span>
                      )}
                    </td>
                    <td style={{ fontSize: 13, color: u.email ? 'var(--me-text-2)' : 'var(--me-text-4)', fontFamily: u.email ? 'var(--me-mono)' : 'inherit' }}>
                      {u.email ?? '—'}
                    </td>
                    <td>
                      <span
                        className={`me-pill ${ROL_COLORS[u.roles?.nombre ?? ''] ?? 'me-pill-neutral'}`}
                        style={u.roles?.nombre === 'administrador' ? { color: '#3a4800' } : undefined}
                      >
                        {u.roles?.nombre ?? '—'}
                      </span>
                    </td>
                    <td>
                      {u.activo
                        ? <span className="me-pill me-pill-ok">Activo</span>
                        : <span className="me-pill me-pill-neutral">Inactivo</span>}
                    </td>
                    <td style={{ fontSize: 12, color: 'var(--me-text-3)' }}>
                      {new Date(u.created_at).toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: '2-digit' })}
                    </td>
                    <td>
                      <button
                        className="me-icon-btn"
                        title="Editar usuario"
                        onClick={() => setEditTarget(u)}
                      >
                        <EditIcon size={14} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modales */}
      {showCreate && (
        <CreateModal
          roles={roles}
          isPending={crearUsuario.isPending}
          onSubmit={form => crearUsuario.mutate(form)}
          onClose={() => setShowCreate(false)}
        />
      )}
      {editTarget && (
        <EditModal
          usuario={editTarget}
          roles={roles}
          isSelf={editTarget.id === user?.id}
          isPending={editarUsuario.isPending}
          onSubmit={form => editarUsuario.mutate({ id: editTarget.id, form })}
          onClose={() => setEditTarget(null)}
        />
      )}
      {lastCreds && (
        <CredentialCard creds={lastCreds} onClose={() => setLastCreds(null)} />
      )}
    </div>
  )
}

// ─── Modal crear usuario ──────────────────────────────────────────────────────

function CreateModal({
  roles, isPending, onSubmit, onClose,
}: {
  roles: Rol[]
  isPending: boolean
  onSubmit: (f: CreateForm) => void
  onClose: () => void
}) {
  const [form, setForm] = useState<CreateForm>({
    nombre:   '',
    email:    '',
    password: '',
    role_id:  roles.find(r => r.nombre === 'cajero')?.id ?? roles[0]?.id ?? '',
  })
  const [showPwd, setShowPwd] = useState(false)

  function field(k: keyof CreateForm) {
    return (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
      setForm(f => ({ ...f, [k]: e.target.value }))
  }

  function handleSubmit() {
    if (!form.nombre.trim()) { toast.error('Ingresá el nombre'); return }
    if (!form.email.trim())  { toast.error('Ingresá el email'); return }
    if (form.password.length < 6) { toast.error('La contraseña debe tener al menos 6 caracteres'); return }
    if (!form.role_id) { toast.error('Seleccioná un rol'); return }
    onSubmit(form)
  }

  return (
    <>
      <div className="me-overlay me-overlay-in" onClick={onClose} />
      <div className="me-modal me-modal-in" style={{ width: 480 }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px 24px', borderBottom: '1px solid var(--me-line-2)' }}>
          <h2 className="me-section-title">Nuevo usuario</h2>
          <button className="me-icon-btn" onClick={onClose}><CloseIcon size={18} /></button>
        </div>

        {/* Body */}
        <div style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div>
            <label className="me-label" style={{ display: 'block', marginBottom: 8 }}>Nombre</label>
            <input className="me-input" style={{ width: '100%' }} value={form.nombre} onChange={field('nombre')} placeholder="Ej: María González" autoFocus />
          </div>
          <div>
            <label className="me-label" style={{ display: 'block', marginBottom: 8 }}>Email</label>
            <input className="me-input" style={{ width: '100%' }} type="email" value={form.email} onChange={field('email')} placeholder="usuario@negocio.com" />
          </div>
          <div>
            <label className="me-label" style={{ display: 'block', marginBottom: 8 }}>Contraseña</label>
            <div style={{ position: 'relative' }}>
              <input
                className="me-input"
                style={{ width: '100%', paddingRight: 40 }}
                type={showPwd ? 'text' : 'password'}
                value={form.password}
                onChange={field('password')}
                placeholder="Mínimo 6 caracteres"
              />
              <button
                type="button"
                onClick={() => setShowPwd(v => !v)}
                style={{
                  position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)',
                  background: 'none', border: 'none', cursor: 'pointer', color: 'var(--me-text-3)',
                  fontSize: 11, fontFamily: 'var(--me-sans)', fontWeight: 600, padding: '2px 4px',
                }}
              >
                {showPwd ? 'Ocultar' : 'Ver'}
              </button>
            </div>
          </div>
          <div>
            <label className="me-label" style={{ display: 'block', marginBottom: 8 }}>Rol</label>
            <RolSelect value={form.role_id} roles={roles} onChange={v => setForm(f => ({ ...f, role_id: v }))} />
          </div>


        </div>

        {/* Footer */}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', padding: '16px 24px', borderTop: '1px solid var(--me-line-2)' }}>
          <button className="me-btn me-btn-ghost" onClick={onClose}>Cancelar</button>
          <button className="me-btn me-btn-primary" onClick={handleSubmit} disabled={isPending}>
            {isPending ? 'Creando…' : 'Crear usuario'}
          </button>
        </div>
      </div>
    </>
  )
}

// ─── Modal editar usuario ─────────────────────────────────────────────────────

function EditModal({
  usuario, roles, isSelf, isPending, onSubmit, onClose,
}: {
  usuario: Profile
  roles: Rol[]
  isSelf: boolean
  isPending: boolean
  onSubmit: (f: EditForm) => void
  onClose: () => void
}) {
  const [form, setForm] = useState<EditForm>({
    nombre:  usuario.nombre,
    role_id: usuario.role_id,
    activo:  usuario.activo,
  })
  const [newPwd, setNewPwd]     = useState('')
  const [showNewPwd, setShowNewPwd] = useState(false)
  const [resetando, setResetando]   = useState(false)

  async function handleResetPwd() {
    if (newPwd.length < 6) { toast.error('Mínimo 6 caracteres'); return }
    setResetando(true)
    try {
      await api.post('/api/auth/forgot-password', { email: usuario.email, newPassword: newPwd })
      toast.success('Contraseña actualizada')
      setNewPwd('')
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Error al cambiar contraseña')
    } finally {
      setResetando(false)
    }
  }

  return (
    <>
      <div className="me-overlay me-overlay-in" onClick={onClose} />
      <div className="me-modal me-modal-in" style={{ width: 440 }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px 24px', borderBottom: '1px solid var(--me-line-2)' }}>
          <h2 className="me-section-title">Editar usuario</h2>
          <button className="me-icon-btn" onClick={onClose}><CloseIcon size={18} /></button>
        </div>

        {/* Body */}
        <div style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 16 }}>
          {usuario.email && (
            <div style={{ padding: '8px 12px', borderRadius: 8, background: 'var(--me-surface-2)', fontSize: 13, color: 'var(--me-text-3)', fontFamily: 'var(--me-mono)' }}>
              {usuario.email}
            </div>
          )}

          <div>
            <label className="me-label" style={{ display: 'block', marginBottom: 8 }}>Nombre</label>
            <input
              className="me-input"
              style={{ width: '100%' }}
              value={form.nombre}
              onChange={e => setForm(f => ({ ...f, nombre: e.target.value }))}
              autoFocus
            />
          </div>

          <div>
            <label className="me-label" style={{ display: 'block', marginBottom: 8 }}>Rol</label>
            <RolSelect
              value={form.role_id}
              roles={roles}
              onChange={v => setForm(f => ({ ...f, role_id: v }))}
              disabled={isSelf}
            />
            {isSelf && (
              <p style={{ fontSize: 11, color: 'var(--me-text-3)', marginTop: 4 }}>
                No podés cambiar tu propio rol
              </p>
            )}
          </div>

          <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: isSelf ? 'not-allowed' : 'pointer' }}>
            <input
              type="checkbox"
              checked={form.activo}
              onChange={e => setForm(f => ({ ...f, activo: e.target.checked }))}
              disabled={isSelf}
              style={{ width: 15, height: 15, accentColor: '#c9f000', cursor: isSelf ? 'not-allowed' : 'pointer' }}
            />
            <span style={{ fontSize: 13.5, fontWeight: 500, color: isSelf ? 'var(--me-text-4)' : 'var(--me-text)' }}>
              Usuario activo
            </span>
          </label>

          {/* Cambiar contraseña */}
          <div style={{ borderTop: '1px solid var(--me-line-2)', paddingTop: 14 }}>
            <label className="me-label" style={{ display: 'block', marginBottom: 8 }}>Nueva contraseña</label>
            <div style={{ display: 'flex', gap: 8 }}>
              <div style={{ position: 'relative', flex: 1 }}>
                <input
                  className="me-input"
                  style={{ width: '100%', paddingRight: 40 }}
                  type={showNewPwd ? 'text' : 'password'}
                  value={newPwd}
                  onChange={e => setNewPwd(e.target.value)}
                  placeholder="Dejar vacío para no cambiar"
                />
                <button
                  type="button"
                  onClick={() => setShowNewPwd(v => !v)}
                  style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--me-text-3)', fontSize: 11, fontFamily: 'var(--me-sans)', fontWeight: 600 }}
                >
                  {showNewPwd ? 'Ocultar' : 'Ver'}
                </button>
              </div>
              <button
                className="me-btn me-btn-ghost"
                onClick={handleResetPwd}
                disabled={!newPwd || resetando}
                style={{ flexShrink: 0 }}
              >
                {resetando ? '…' : 'Aplicar'}
              </button>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', padding: '16px 24px', borderTop: '1px solid var(--me-line-2)' }}>
          <button className="me-btn me-btn-ghost" onClick={onClose}>Cancelar</button>
          <button className="me-btn me-btn-primary" onClick={() => onSubmit(form)} disabled={isPending}>
            {isPending ? 'Guardando…' : 'Guardar cambios'}
          </button>
        </div>
      </div>
    </>
  )
}

// ─── Card de credenciales post-creación ───────────────────────────────────────

function CredentialCard({
  creds, onClose,
}: {
  creds: { nombre: string; email: string; password: string }
  onClose: () => void
}) {
  function copiar() {
    const texto = `Usuario: ${creds.nombre}\nEmail: ${creds.email}\nContraseña: ${creds.password}`
    navigator.clipboard.writeText(texto).then(() => toast.success('Credenciales copiadas'))
  }

  return (
    <>
      <div className="me-overlay me-overlay-in" onClick={onClose} />
      <div className="me-modal me-modal-in" style={{ width: 420 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px 24px', borderBottom: '1px solid var(--me-line-2)' }}>
          <h2 className="me-section-title">Usuario creado</h2>
          <button className="me-icon-btn" onClick={onClose}><CloseIcon size={18} /></button>
        </div>
        <div style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 14 }}>
          <p style={{ fontSize: 13, color: 'var(--me-text-3)', margin: 0 }}>
            Guardá estas credenciales — la contraseña no se puede ver después.
          </p>
          <div style={{
            background: 'var(--me-surface-2)', border: '1px solid var(--me-line)',
            borderRadius: 10, padding: '16px 18px', display: 'flex', flexDirection: 'column', gap: 10,
          }}>
            {[
              { label: 'Nombre',     value: creds.nombre },
              { label: 'Email',      value: creds.email },
              { label: 'Contraseña', value: creds.password },
            ].map(({ label, value }) => (
              <div key={label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: 12, color: 'var(--me-text-3)', fontWeight: 600 }}>{label}</span>
                <span style={{ fontSize: 13.5, fontFamily: 'var(--me-mono)', color: 'var(--me-text)', letterSpacing: label === 'Contraseña' ? 1 : 0 }}>
                  {value}
                </span>
              </div>
            ))}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', padding: '16px 24px', borderTop: '1px solid var(--me-line-2)' }}>
          <button className="me-btn me-btn-ghost" onClick={copiar}>Copiar credenciales</button>
          <button className="me-btn me-btn-primary" onClick={onClose}>Entendido</button>
        </div>
      </div>
    </>
  )
}

// ─── Select de rol con chevron ────────────────────────────────────────────────

function RolSelect({
  value, roles, onChange, disabled = false,
}: {
  value: string
  roles: Rol[]
  onChange: (v: string) => void
  disabled?: boolean
}) {
  return (
    <div style={{ position: 'relative' }}>
      <select
        className="me-input"
        style={{ width: '100%', appearance: 'none', paddingRight: 32, cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.5 : 1 }}
        value={value}
        onChange={e => onChange(e.target.value)}
        disabled={disabled}
      >
        {roles.map(r => (
          <option key={r.id} value={r.id}>
            {r.nombre.charAt(0).toUpperCase() + r.nombre.slice(1)} (nivel {r.nivel})
          </option>
        ))}
      </select>
      <span style={{
        position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)',
        pointerEvents: 'none', color: 'var(--me-text-3)', display: 'flex',
      }}>
        <ChevronDownIcon size={14} />
      </span>
    </div>
  )
}
